/**
 * @file PricingGroupCosts.js
 * @version 1.0.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-28
 * @modified 2026-10-28
 * @description Pricing & Costing: a product group's costs this period, in a
 *              line's or a product group's options (Cam, 28 Oct 2026 — P9 and
 *              the redesign's D6, "Each supplier's latest bill; the highest
 *              marked … the margin is set against it, so the price makes money
 *              whichever mill supplies it").
 *
 *   Costs (GET /api/product-groups/:id/costs — services/productGroupCostService):
 *   each member's latest bill this period per tonne and per its own unit, the
 *   range of earlier bills, how many bills and tonnes; the highest in this
 *   line's pack marked; the customer's usual supplier and how often. The
 *   margin the line's price makes against each supplier's cost, the highest
 *   and the usual, per tonne. Costs only for costs.view (the route nulls them
 *   by name; the panel says so).
 *
 *   What OTHER customers pay for the group this period (GET
 *   /api/pricing-costing/group-prices) — the office only (Cam: reps "No reps
 *   should definitely not"; Pricing & Costing refuses reps outright): each
 *   customer's newest price per its unit and per tonne, contract or not.
 *
 *   Read only. Fetched when the panel first shows, cached per group, customer
 *   and day for the session of the screen.
 *
 *   1.0.1 (2026-10-28, N6): another customer's newest price says what it is
 *   per by the one rule (utils/unitPriceLabel.perUnitOfLabel): " each" for
 *   None, " (unit not set)" when unknown (were "/each", "/unit?").
 */

import React, { useEffect, useState } from 'react';
import { API_URL } from '../config';
import { money } from '../utils/pricingCosting';
import { perUnitOfLabel } from '../utils/unitPriceLabel';
import { formatDate } from '../utils/dateFormatters';
import { formatTonnes } from '../utils/numberFormatters';

const cache = new Map();
const fetchJson = (url) => {
  if (!cache.has(url)) {
    cache.set(url, fetch(url, { credentials: 'include' })
      .then(r => r.json().then(b => (r.ok && b.success ? b : Promise.reject(new Error(b.error || 'Not read')))))
      .catch(err => { cache.delete(url); throw err; }));
  }
  return cache.get(url);
};

const num = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
const marginBits = (price, cost) => {
  const p = num(price);
  const c = num(cost);
  if (p === null || c === null || !(c > 0)) return null;
  return { abs: p - c, pc: ((p - c) / c) * 100 };
};

function MarginText({ price, cost }) {
  const m = marginBits(price, cost);
  if (!m) return <span className="pc-muted">—</span>;
  return (
    <span className={m.abs < 0 ? 'pc-neg' : m.pc < 8 ? 'pc-gc-low' : 'pc-gc-ok'}>
      {money(Math.round(m.abs * 100) / 100)}/T <span className="pc-muted">{m.pc.toFixed(1)}%</span>
    </span>
  );
}

/**
 * @param {{ groupId, groupName, accountId, accountName, date, pack, salePerT, seeCosts }} props
 *   salePerT  the line's (or group's) sale price per tonne now, or null
 */
function PricingGroupCosts({ groupId, groupName, accountId, accountName, date, pack = null, salePerT = null, seeCosts = true }) {
  const [costs, setCosts] = useState({ loading: true, error: null, body: null });
  const [prices, setPrices] = useState({ loading: true, error: null, body: null });

  useEffect(() => {
    let gone = false;
    const qs = new URLSearchParams({ ...(accountId ? { accountId: String(accountId) } : {}), ...(date ? { date } : {}) });
    fetchJson(`${API_URL}/api/product-groups/${groupId}/costs?${qs}`)
      .then(body => { if (!gone) setCosts({ loading: false, error: null, body }); })
      .catch(err => { if (!gone) setCosts({ loading: false, error: err.message, body: null }); });
    return () => { gone = true; };
  }, [groupId, accountId, date]);

  const period = costs.body?.group?.period || null;
  useEffect(() => {
    if (!period?.from || !period?.to) {
      if (!costs.loading) setPrices({ loading: false, error: null, body: null });
      return undefined;
    }
    let gone = false;
    const qs = new URLSearchParams({ groupId: String(groupId), from: period.from, to: period.to, ...(accountId ? { accountId: String(accountId) } : {}) });
    fetchJson(`${API_URL}/api/pricing-costing/group-prices?${qs}`)
      .then(body => { if (!gone) setPrices({ loading: false, error: null, body }); })
      .catch(err => { if (!gone) setPrices({ loading: false, error: err.message, body: null }); });
    return () => { gone = true; };
  }, [groupId, accountId, period?.from, period?.to, costs.loading]);

  if (costs.loading) return <div className="pc-gc"><p className="pc-muted">Reading {groupName || 'the group'}'s costs this period…</p></div>;
  if (costs.error) return <div className="pc-gc"><p className="pc-flag err"><span className="material-symbols-rounded">error</span>{costs.error}</p></div>;
  if (costs.body && costs.body.ready === false) return null;
  const g = costs.body?.group;
  if (!g) return null;
  const usual = costs.body.usual;
  const items = g.items || [];
  const hi = (pack && g.highest_by_pack?.[pack]) || g.highest;
  const hiCost = hi ? num(hi.highest_cost_per_t) : null;
  const usualCost = usual ? num(usual.usual_cost_per_t) : null;
  const shown = items.filter(i => num(i.cost_per_t) !== null || i.usual || !i.is_archived);
  const others = prices.body?.customers || [];

  return (
    <div className="pc-gc">
      <h5>
        <span className="material-symbols-rounded">stacks</span>
        {g.name} · {items.length} product{items.length === 1 ? '' : 's'} · cost this period
        <span className="pc-muted"> {period?.name ? `${period.name} (from ${formatDate(period.from)})` : ''} · per tonne</span>
      </h5>
      {!seeCosts && <p className="pc-muted">Costs are hidden — needs permission to see costs.</p>}
      {seeCosts && (
        <table className="pc-gc-table">
          <thead>
            <tr>
              <th>Supplier · product</th>
              <th className="r">Latest bill</th>
              <th className="r">This period</th>
              <th className="r">Bills · tonnes</th>
              <th className="r">Margin at {salePerT !== null ? `${money(salePerT)}/T` : '—'}</th>
            </tr>
          </thead>
          <tbody>
            {shown.map(i => (
              <tr key={i.item_id} className={i.highest || (hi && hi.item_id === i.item_id) ? 'hi' : ''}>
                <td>
                  <b>{i.mill || i.supplier || 'Supplier not known'}</b>
                  {hi && hi.item_id === i.item_id && <span className="pc-gc-tag hi">Highest</span>}
                  {i.usual && <span className="pc-gc-tag usual">Usual</span>}
                  <br /><span className="pc-muted">{i.name} · {i.code}{i.additive ? ` · ${i.additive}` : ''}{i.pack_words ? ` · ${i.pack_words}` : ''}{i.is_archived ? ' · archived' : ''}</span>
                </td>
                <td className="r">
                  {num(i.cost_per_t) === null ? <span className="pc-muted">No bill this period</span> : (
                    <>
                      <b>{money(i.cost_per_t)}/T</b>
                      {num(i.cost_per_unit) !== null && i.unit && num(i.unit_kg) !== 1000 && <span className="pc-muted"> = {money(i.cost_per_unit)}/{i.unit}</span>}
                      <br /><span className="pc-muted">{i.latest_bill?.bill_no || 'Bill'}{i.latest_bill?.delivery_date ? ` · delivered ${formatDate(i.latest_bill.delivery_date)}` : i.latest_bill?.bill_date ? ` · ${formatDate(i.latest_bill.bill_date)}` : ''}</span>
                    </>
                  )}
                </td>
                <td className="r">{num(i.min_per_t) === null ? '—' : (num(i.max_per_t) - num(i.min_per_t) < 0.005 ? `${money(i.min_per_t)}` : `${money(i.min_per_t)}–${money(i.max_per_t)}`)}</td>
                <td className="r">{i.bills || 0} · {formatTonnes(i.tonnes_billed || 0)}</td>
                <td className="r"><MarginText price={salePerT} cost={i.cost_per_t} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {seeCosts && (
        <div className="pc-gc-calc">
          {salePerT === null
            ? <span className="pc-muted">Choose or type a price to see the margin it makes on each.</span>
            : (
              <>
                <span>At {money(salePerT)}/T — on the highest{hi?.supplier ? ` (${hi.supplier}` : ''}{hiCost !== null ? ` ${money(hiCost)}/T)` : hi?.supplier ? ')' : ''}: <MarginText price={salePerT} cost={hiCost} /></span>
                {usual?.usual_supplier && <span>on the usual ({usual.usual_supplier}{usualCost !== null ? ` ${money(usualCost)}/T` : ''}): <MarginText price={salePerT} cost={usualCost} /></span>}
              </>
            )}
        </div>
      )}
      <p className="pc-muted pc-gc-usual">
        <span className="material-symbols-rounded">person</span>
        {accountName ? `${accountName}: ` : ''}{usual?.text || 'No deliveries of this group to this customer in the last 12 months'}
      </p>
      <div className="pc-gc-others">
        <h6>What other customers pay for {g.name} this period <span className="pc-muted">— the office only, never shown to reps</span></h6>
        {prices.loading ? <p className="pc-muted">Reading…</p> : prices.error ? (
          <p className="pc-flag err"><span className="material-symbols-rounded">error</span>{prices.error}</p>
        ) : !others.length ? <p className="pc-muted">No sale of this group this period.</p> : (
          <table className="pc-gc-table">
            <thead>
              <tr><th>Customer</th><th className="r">Newest price</th><th className="r">Per tonne</th><th>Contract</th><th>When</th></tr>
            </thead>
            <tbody>
              {others.slice(0, 12).map(c => (
                <tr key={c.account_id} className={c.this_customer ? 'mine' : ''}>
                  <td>{c.account_no ? <span className="account-badge-inline">{c.account_no}</span> : null} {c.name}{c.this_customer ? <span className="pc-muted"> (this customer)</span> : null}
                    {c.lines > 1 && <span className="pc-muted"> · {c.lines} lines</span>}</td>
                  <td className="r">{money(c.latest?.price)}{perUnitOfLabel(c.latest?.unit).suffix}</td>
                  <td className="r">{c.latest?.per_t !== null && c.latest?.per_t !== undefined ? `${money(c.latest.per_t)}/T` : '—'}
                    {c.min_per_t !== null && c.max_per_t !== null && c.max_per_t - c.min_per_t >= 0.005 && <span className="pc-muted"> ({money(c.min_per_t)}–{money(c.max_per_t)})</span>}</td>
                  <td>{c.latest?.contract ? (c.latest.contract === 'contract' ? 'Yes' : c.latest.contract) : <span className="pc-muted">No</span>}{c.on_contract && c.on_contract < c.lines ? <span className="pc-muted"> ({c.on_contract} of {c.lines})</span> : null}</td>
                  <td>{formatDate(c.latest?.date)} <span className="pc-muted">{c.latest?.ref}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {others.length > 12 && <p className="pc-muted">and {others.length - 12} more customers</p>}
      </div>
    </div>
  );
}

export default PricingGroupCosts;
